import { Injectable } from '@angular/core';
import { HomeSettings } from '@local/client-contracts';
import { observable } from '@local/common';
import { LogService, ServicesRpcService } from '@shared/services';
import { Logger } from '@unleash-tech/js-logger';
import { ReplaySubject, distinctUntilChanged, map, firstValueFrom } from 'rxjs';
import { HomeSettingsRpcInvoker } from './invokers/home-settings.rpc-invoker';
import { cloneDeep, isEqual } from 'lodash';
import { SessionService } from '@shared/services/session.service';

@Injectable({
  providedIn: 'root',
})
export class HomeSettingsService {
  private logger: Logger;
  private service: HomeSettings.Service;
  private _settings$ = new ReplaySubject<HomeSettings.HomeSettings>(1);

  constructor(
    services: ServicesRpcService,
    logger: LogService,
    private sessionService: SessionService
  ) {
    this.logger = logger.scope('HomeSettingsService');
    this.service = services.invokeWith(HomeSettingsRpcInvoker, 'homesettings');
    this.service.all$.subscribe((all) => {
      this._settings$.next(all?.[0]);
    });
  }

  @observable
  get orderTabs$() {
    return this._settings$.pipe(
      distinctUntilChanged((prev, next) => {
        return isEqual(prev, next);
      }),
      map((s) => s?.tabIds || [])
    );
  }

  update(homeSettingId: string, actions: HomeSettings.UpdateAction[]): Promise<void> {
    return this.service.update(homeSettingId, actions);
  }

  async create(data: HomeSettings.HomeSettings): Promise<void> {
    await this.service.create(data);
  }

  refresh(): Promise<void> {
    return this.service.refresh();
  }

  async updateTabs(tabIds: string[]) {
    const settings = cloneDeep(await firstValueFrom(this._settings$));
    this._settings$.next({ ...settings, tabIds });
    if (settings?.id) {
      return this.update(settings?.id, [{ field: 'tabIds', value: tabIds, type: 'Update' }]);
    } else {
      const session = await firstValueFrom(this.sessionService.current$);
      return this.create({ id: session.workspace.accountId, tabIds: tabIds });
    }
  }

  async addTab(newTabId: string) {
    const settings = await firstValueFrom(this._settings$);
    const tabIds = cloneDeep(settings?.tabIds) || [];
    tabIds.push(newTabId);
    return this.updateTabs(tabIds);
  }

  async removeTab(tabId: string) {
    const settings = await firstValueFrom(this._settings$);
    const tabIds = cloneDeep(settings?.tabIds) || [];
    const updatedTabs = tabIds.filter((t) => t !== tabId);
    if (updatedTabs.length !== tabIds.length) {
      return this.updateTabs(updatedTabs);
    }
  }
}
